import { Component, HostListener, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, RouterLink } from '@angular/router';
import { LanguageCode } from '../../core/i18n/translation.types';
import { TranslationService } from '../../core/i18n/translation.service';

@Component({
  selector: 'app-header',
  standalone: true,
  imports: [CommonModule, RouterLink],
  templateUrl: './header.component.html',
  styleUrl: './header.component.scss',
})
export class HeaderComponent {
  readonly i18n = inject(TranslationService);
  private readonly router = inject(Router);

  isScrolled = false;
  isMobileMenuOpen = false;
  isLangDropdownOpen = false;
  isCompanyDropdownOpen = false;

  readonly topLinkConfig = [
    { key: 'subscribe' as const, href: '#', external: true },
    { key: 'support' as const, href: '#', external: false },
    { key: 'corporate' as const, href: '#', external: true },
  ];

  readonly navLinkConfig = [
    { key: 'products' as const, href: '#solutions', hasDropdown: true },
    { key: 'applications' as const, href: '#solutions', hasDropdown: true },
    { key: 'research' as const, href: '#stats', hasDropdown: false },
    { key: 'resources' as const, href: '#blog', hasDropdown: true },
    { key: 'company' as const, href: '/company', hasDropdown: true },
  ];

  @HostListener('window:scroll')
  onScroll(): void {
    this.isScrolled = window.scrollY > 50;
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    if (this.isMobileMenuOpen) {
      this.closeMobileMenu();
    }
    this.isLangDropdownOpen = false;
    this.isCompanyDropdownOpen = false;
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: Event): void {
    const target = event.target as HTMLElement;
    if (!target.closest('.lang-dropdown')) {
      this.isLangDropdownOpen = false;
    }
    if (!target.closest('.company-dropdown')) {
      this.isCompanyDropdownOpen = false;
    }
  }

  toggleMobileMenu(): void {
    this.isMobileMenuOpen = !this.isMobileMenuOpen;
    this.setBodyScroll(this.isMobileMenuOpen);
  }

  closeMobileMenu(): void {
    this.isMobileMenuOpen = false;
    this.setBodyScroll(false);
  }

  toggleLangDropdown(event: Event): void {
    event.stopPropagation();
    this.isLangDropdownOpen = !this.isLangDropdownOpen;
    this.isCompanyDropdownOpen = false;
  }

  toggleCompanyDropdown(event: Event): void {
    event.stopPropagation();
    this.isCompanyDropdownOpen = !this.isCompanyDropdownOpen;
    this.isLangDropdownOpen = false;
  }

  closeCompanyDropdown(): void {
    this.isCompanyDropdownOpen = false;
  }

  goToAboutPage(event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    void this.router.navigateByUrl('/company');
    this.isCompanyDropdownOpen = false;
    this.isMobileMenuOpen = false;
    this.setBodyScroll(false);
  }

  goToHomePage(event: Event): void {
    const isHome = this.router.url === '/' || this.router.url === '';

    if (isHome) {
      event.preventDefault();
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }

    this.isCompanyDropdownOpen = false;
    this.closeMobileMenu();
  }

  selectLanguage(code: LanguageCode): void {
    this.i18n.setLanguage(code);
    this.isLangDropdownOpen = false;
  }

  private setBodyScroll(locked: boolean): void {
    document.body.style.overflow = locked ? 'hidden' : '';
  }
}
